import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CreditCard, AlertTriangle, Clock, 
  FileText, Truck, ChevronRight,
  Calendar, ExternalLink, CheckCircle2, Package
} from 'lucide-react';
import { encours, factures, bonsLivraison, historiqueReglements, clientConnecte, formatMontant, formatDate, getStatutLabel, getStatutColor } from '../data/mockData';

const Dashboard = () => {
  const navigate = useNavigate();
  const facturesRecentes = factures.slice(0, 3);
  const livraisonsRecentes = bonsLivraison.slice(0, 3);

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Tableau de bord</h1>
          <p className="page-subtitle">Bienvenue, {clientConnecte.prenom} {clientConnecte.nom} — {clientConnecte.raisonSociale}</p>
        </div>
        <div className="header-date">
          <Calendar size={16} />
          <span>{new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
        </div>
      </div>

      {/* Cartes KPI */}
      <div className="kpi-grid">
        <div className="kpi-card kpi-primary">
          <div className="kpi-icon-wrapper kpi-icon-amber">
            <CreditCard size={24} />
          </div>
          <div className="kpi-content">
            <span className="kpi-label">Encours total</span>
            <span className="kpi-value">{formatMontant(encours.totalDu)}</span>
            <span className="kpi-detail">{factures.filter(f => f.statut !== 'payee').length} facture(s) en cours</span>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-icon-wrapper kpi-icon-red">
            <AlertTriangle size={24} />
          </div>
          <div className="kpi-content">
            <span className="kpi-label">Factures en retard</span>
            <span className="kpi-value kpi-value-danger">{encours.facturesEnRetard}</span>
            <span className="kpi-detail">{formatMontant(encours.montantRetard)}</span>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-icon-wrapper kpi-icon-blue">
            <Clock size={24} />
          </div>
          <div className="kpi-content">
            <span className="kpi-label">Prochaine échéance</span>
            <span className="kpi-value">{formatDate(encours.echeanceProcheDate)}</span>
            <span className="kpi-detail">{formatMontant(encours.echeanceProcheMontant)}</span>
          </div>
        </div>
      </div>

      {/* Paiement en ligne */}
      {encours.totalDu > 0 && (
        <div className="payment-banner">
          <div className="payment-banner-content">
            <div className="payment-banner-icon">
              <CreditCard size={28} />
            </div>
            <div className="payment-banner-text">
              <h3>Réglez votre encours en ligne</h3>
              <p>Paiement sécurisé par carte bancaire — Montant dû : <strong>{formatMontant(encours.totalDu)}</strong></p>
            </div>
          </div>
          <button className="payment-banner-btn" onClick={() => window.open('#paiement-cb', '_blank')}>
            <CreditCard size={18} />
            Payer par carte bancaire
            <ExternalLink size={14} />
          </button>
        </div>
      )}

      <div className="dashboard-grid">
        {/* Factures récentes */}
        <div className="dashboard-card">
          <div className="dashboard-card-header">
            <div className="dashboard-card-title">
              <FileText size={20} />
              <h2>Dernières factures</h2>
            </div>
            <button className="link-btn" onClick={() => navigate('/documents?tab=factures')}>
              Tout voir <ChevronRight size={16} />
            </button>
          </div>
          <div className="dashboard-card-body">
            {facturesRecentes.map((fac) => {
              const statutStyle = getStatutColor(fac.statut);
              return (
                <div key={fac.id} className="doc-row" onClick={() => navigate(`/documents/${fac.id}`)}>
                  <div className="doc-row-left">
                    <div className="doc-row-icon facture-icon">
                      <FileText size={16} />
                    </div>
                    <div>
                      <p className="doc-row-id">{fac.id}</p>
                      <p className="doc-row-ref">{fac.reference}</p>
                    </div>
                  </div>
                  <div className="doc-row-right">
                    <span className="doc-row-amount">{formatMontant(fac.montantTTC)}</span>
                    <span 
                      className="doc-status-badge"
                      style={{ background: statutStyle.bg, color: statutStyle.color, borderColor: statutStyle.border }}
                    >
                      {getStatutLabel(fac.statut)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Livraisons récentes */}
        <div className="dashboard-card">
          <div className="dashboard-card-header">
            <div className="dashboard-card-title">
              <Truck size={20} />
              <h2>Dernières livraisons</h2>
            </div>
            <button className="link-btn" onClick={() => navigate('/documents?tab=livraisons')}>
              Tout voir <ChevronRight size={16} />
            </button>
          </div>
          <div className="dashboard-card-body">
            {livraisonsRecentes.map((bl) => (
              <div key={bl.id} className="doc-row" onClick={() => navigate(`/documents/${bl.id}`)}>
                <div className="doc-row-left">
                  <div className="doc-row-icon livraison-icon">
                    <Truck size={16} />
                  </div>
                  <div>
                    <p className="doc-row-id">{bl.id}</p>
                    <p className="doc-row-ref">{bl.reference}</p>
                  </div>
                </div>
                <div className="doc-row-right">
                  <span className="doc-row-date">{formatDate(bl.date)}</span>
                  <span className="doc-status-badge" style={{ background: '#D1FAE5', color: '#065F46', borderColor: '#A7F3D0' }}>
                    <CheckCircle2 size={12} /> {bl.statut === 'livre' ? 'Livré' : bl.statut}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Historique des règlements */}
      <div className="dashboard-card" style={{ marginTop: '1.5rem' }}>
        <div className="dashboard-card-header">
          <div className="dashboard-card-title">
            <Package size={20} />
            <h2>Derniers règlements</h2>
          </div>
        </div>
        <div className="dashboard-card-body">
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Référence</th>
                <th>Mode</th>
                <th style={{ textAlign: 'right' }}>Montant</th>
              </tr>
            </thead>
            <tbody>
              {historiqueReglements.map((reg, idx) => (
                <tr key={idx}>
                  <td>{formatDate(reg.date)}</td>
                  <td><span className="mono-text">{reg.reference}</span></td>
                  <td>{reg.mode}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatMontant(reg.montant)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
